import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("crm_token");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      if (err?.response?.status === 401) localStorage.removeItem("crm_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Proactively refresh whenever the app comes back to the foreground (a PWA left
  // backgrounded for hours is exactly the case that used to hard-expire silently) —
  // this is on top of the reactive 401-triggered refresh in api.js, not instead of it,
  // so a stale token never even gets a chance to fail a real request first.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && localStorage.getItem("crm_token")) {
        api.post("/auth/refresh").then(({ data }) => {
          localStorage.setItem("crm_token", data.access_token);
          setUser(data.user);
        }).catch(() => { /* reactive interceptor will handle it on next real request */ });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("crm_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("crm_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}
